/**
 * Records which product gallery images a shopper actually sees.
 *
 * Loaded by sections/main-product.liquid only when the product has more than one media item.
 * Each image counts once per page view, when at least 60% of it has been on screen for half a
 * second. That covers swipes, arrows, thumbnails and (in stacked layouts) page scroll alike, and
 * works on /ar, where the slider scrolls right to left. A fast swipe past an image does not count.
 *
 * Each view is sent as:
 * - GA4: event `gallery_image_<n>` (n = position, 1-based, capped at 10), sent only to the GA4
 *   property the Google & YouTube app configured (found in dataLayer, so no id lives here).
 *   The position is in the event name so reports need no custom dimension; the parameters
 *   `image_position`, `image_count` and `product_type` are there if one is registered later.
 * - Clarity: custom event `gallery_image_<n>`, and the session tag `gallery_max_image`, the
 *   furthest image seen, for filtering recordings.
 */
(() => {
  const script = document.currentScript;
  const productType = script ? script.dataset.productType || '' : '';
  const gallery = document.querySelector('#product-media media-gallery');
  if (!gallery || !('IntersectionObserver' in window)) return;

  const items = Array.from(gallery.querySelectorAll('.media-viewer__item'));
  if (items.length < 2) return;

  const DWELL_MS = 500;
  const seen = new Set();
  const timers = new Map();
  let maxSeen = 0;

  /**
   * Returns the GA4 measurement id the Google & YouTube app configured, if it has loaded.
   * @returns {?string}
   */
  const ga4Id = () => {
    const entry = (window.dataLayer || []).find((args) => args && args[0] === 'config'
      && typeof args[1] === 'string' && args[1].startsWith('G-'));
    return entry ? entry[1] : null;
  };

  /**
   * Sends the event to GA4, retrying for a while if the Google tag has not loaded yet.
   * @param {string} name - Event name.
   * @param {object} params - Event parameters.
   * @param {number} [tries=0] - Attempts made so far.
   */
  const sendGa4 = (name, params, tries = 0) => {
    const id = ga4Id();
    if (typeof window.gtag === 'function' && id) {
      window.gtag('event', name, { ...params, send_to: id });
    } else if (tries < 15) {
      setTimeout(() => sendGa4(name, params, tries + 1), 1000);
    }
  };

  /**
   * Records one image view.
   * @param {number} position - 1-based position of the image in the gallery.
   */
  const record = (position) => {
    const name = `gallery_image_${Math.min(position, 10)}`;
    sendGa4(name, {
      image_position: position,
      image_count: items.length,
      product_type: productType
    });

    if (typeof window.clarity === 'function') {
      window.clarity('event', name);
      if (position > maxSeen) {
        maxSeen = position;
        window.clarity('set', 'gallery_max_image', String(maxSeen));
      }
    }
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const position = items.indexOf(entry.target) + 1;
      if (seen.has(position)) return;

      if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
        if (timers.has(position)) return;
        timers.set(position, setTimeout(() => {
          timers.delete(position);
          seen.add(position);
          observer.unobserve(entry.target);
          record(position);
        }, DWELL_MS));
      } else if (timers.has(position)) {
        clearTimeout(timers.get(position));
        timers.delete(position);
      }
    });
  }, { threshold: [0, 0.6] });

  items.forEach((item) => observer.observe(item));
})();
